import { AsyncPipe, CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { first, firstValueFrom, map } from "rxjs";
import { UserProfile } from "src/app/subscription/services/auth/auth.interface";
import { AuthService } from "src/app/subscription/services/auth/auth.service";
import { FormlyAvatarModule } from "../ui/formly-avatar/formly-avatar.module";

type ProfileFormModel = Pick<UserProfile, "avatar_url" | "display_name">;

@Component({
  selector: "app-profile-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule,
    FormlyAvatarModule,
    AsyncPipe,
  ],
  templateUrl: "./profile-modal.component.html",
  styleUrl: "./profile-modal.component.scss",
})
export class ProfileModalComponent {
  private authService = inject(AuthService);
  modalRef = inject(NgbActiveModal);

  form = new FormGroup({});
  isLoading = false;
  error?: string;

  formFields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: "row",
      fieldGroup: [
        {
          className: "col-md-8",
          fieldGroup: [
            {
              key: "display_name",
              type: "input",
              props: {
                label: "Display Name",
                placeholder: "Enter your display name",
              },
            },
          ],
        },
        {
          className: "col-md-4 d-flex justify-content-center",
          key: "avatar_url",
          type: "avatar",
          props: {
            label: "Avatar",
            compact: true,
          },
        },
      ],
    },
  ];

  model$ = this.authService.$user.pipe(
    first(),
    map((user) => {
      if (!user) return {};
      return {
        avatar_url: user.avatar_url ?? null,
        display_name: user.display_name ?? null,
      };
    }),
  );

  closeModal() {
    this.modalRef.dismiss();
  }

  async save() {
    if (!this.form.valid) return;

    this.isLoading = true;
    this.error = undefined;

    const user = await firstValueFrom(this.authService.$user);
    if (!user) {
      this.error = "Not logged in";
      this.isLoading = false;
      return;
    }

    const formData = this.form.value as ProfileFormModel;
    const profileData: Partial<UserProfile> = {
      avatar_url: formData.avatar_url || undefined,
      display_name: formData.display_name,
    };

    try {
      await firstValueFrom(
        this.authService.updateUserProfile(user.id, profileData),
      );
      await this.authService.refreshUser();
      this.modalRef.close(true);
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : "Failed to save";
    } finally {
      this.isLoading = false;
    }
  }
}
